import { get, set, trimEnd } from 'lodash';
import { getCanonicalPostUrl, getPostAmpUrl, getPostExcerpt, getPostSeoDescription, } from '../store/post/post-selectors';
import { capitalizeLanguageCode } from './capitalize-language-code';
import { getImageUrl } from './image-utils';
import { resolveId } from './resolve-id';
export const getFullLanguageCode = (siteLanguages = [], language = 'en') => {
    const locale = siteLanguages.find((siteLang) => siteLang.languageCode === language);
    let langCode;
    if (locale) {
        if ('seoLang' in locale) {
            langCode = locale.seoLang ?? undefined;
        }
        else if ('format' in locale) {
            langCode = locale.format ?? undefined;
        }
    }
    langCode ||= language;
    return capitalizeLanguageCode(langCode);
};
const getPostFlattenedCategories = (post, categoryMap) => (post.categoryIds ?? [])
    .map((id) => categoryMap[id])
    .filter(Boolean)
    .map((category) => getCategoryLabel(category))
    .join(', ');
export const toSeoTranslatedPage = (translation, siteLanguages) => {
    return {
        seoData: translation.seoData,
        url: `${translation.url?.base}${translation.url?.path}`,
        langCode: getFullLanguageCode(siteLanguages, translation.language ?? undefined),
    };
};
const getDateForSEOTags = (date) => {
    return date instanceof Date ? date.toISOString() : date;
};
export const getCustomExcerpt = (post) => {
    if (post.hasOwnProperty('customExcerpt')) {
        return post.customExcerpt === true ? post.excerpt : undefined;
    }
    return post.excerpt;
};
const getCategoryLabel = (category) => {
    if (!category) {
        return '';
    }
    if ('menuLabel' in category) {
        return category.menuLabel;
    }
    return category.label;
};
export const generatePostPageSEOTags = ({ appConfig, post, categoriesMap, customPostUrl, mediaUrls, multilingual, ownerUrl, translatedPages, isTogglePostIndexSEOExperimentEnabled, }) => {
    const { postPageSectionUrl, ampBaseUrl, publisher } = appConfig;
    const { slug, seoDescription, title, seoTitle, lastPublishedDate, firstPublishedDate, owner = {}, } = post;
    const mainCategoryFallback = post.categoryIds?.[0] && post.categoryIds[0] in categoriesMap
        ? categoriesMap[post.categoryIds[0]]
        : undefined;
    const mainCategoryLabel = post.mainCategory?.label ?? getCategoryLabel(mainCategoryFallback);
    const excerpt = getCustomExcerpt(post);
    const itemData = {
        post: {
            id: post.id,
            title: seoTitle || title,
            language: getFullLanguageCode(multilingual?.siteLanguages ?? [], post.language ?? undefined),
            description: getPostSeoDescription({
                seoDescription,
                content: post.content,
                excerpt,
            }),
            amphtml: getPostAmpUrl(resolveId(post), ampBaseUrl, slug),
            type: 'article',
            canonicalUrl: getCanonicalPostUrl({
                post,
                postPageSectionUrl,
                customPostUrl,
                multilingual,
            }),
            optInMeta: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
            slug,
            excerpt: getPostExcerpt({
                excerpt,
                content: post.content,
            }),
            lastPublishedDate: getDateForSEOTags(lastPublishedDate),
            firstPublishedDate: getDateForSEOTags(firstPublishedDate),
            mainCategory: mainCategoryLabel,
            categories: getPostFlattenedCategories(post, categoriesMap),
            owner: {
                name: owner.name,
                url: ownerUrl,
            },
            path: customPostUrl || `${trimEnd(postPageSectionUrl, '/')}/${post.slug}`,
            contentImages: mediaUrls,
        },
        postsSettings: {
            isAmpEnabled: Boolean(ampBaseUrl),
            isSchemaEnabled: true,
        },
        translatedPages,
    };
    if (owner.image) {
        set(itemData, 'post.owner.image.url', typeof owner.image === 'string'
            ? owner.image
            : getImageUrl({ image: owner.image }));
    }
    if (publisher) {
        set(itemData, 'publisher', {
            id: publisher['@id'],
            name: publisher.name,
            logo: publisher.logo?.url,
        });
    }
    const isPostShownInSearch = get(post, 'seoShowInSearch', true);
    if (isTogglePostIndexSEOExperimentEnabled) {
        if (!isPostShownInSearch) {
            set(itemData.post, 'noIndex', true);
        }
    }
    if (post.media?.displayed) {
        // @ts-expect-error
        itemData.post.image = {
            url: getImageUrl({ image: post.media.wixMedia?.image }) ||
                post.media.embedMedia?.thumbnail?.url,
            width: post.media.wixMedia?.image?.width ||
                post.media.embedMedia?.thumbnail?.width,
            height: post.media.wixMedia?.image?.height ||
                post.media.embedMedia?.thumbnail?.height,
        };
    }
    return itemData;
};
//# sourceMappingURL=generate-seo-tags.js.map